import {
  Badge, Button, Card, DataTable, DateInput, ErrorState, Field, Input, PageHeader, Pager,
  Permissions, Row, Select, Stack, Textarea, incidentTone, usePermission, usePrintedFile,
  useApi, useClampedPage, useI18n,
  type AnnouncementAudience, type AnnouncementResponse, type IncidentResponse,
} from '@daghfal/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CalendarManager } from '../components/CalendarManager';
import { AttendanceReport, DailyCareReport, RosterReport } from '../components/ReportViews';

type Tab = 'announcements' | 'calendar' | 'incidents' | 'attendance' | 'dailyCare' | 'roster' | 'audit';

const TABS: readonly Tab[] = ['announcements', 'calendar', 'incidents', 'attendance', 'dailyCare', 'roster', 'audit'];

const TAB_TITLES: Record<Tab, string> = {
  announcements: 'announcements.title',
  calendar: 'calendar.title',
  incidents: 'incidents.title',
  attendance: 'reports.attendance.title',
  dailyCare: 'reports.dailyCare.title',
  roster: 'reports.roster.title',
  audit: 'audit.title',
};

const AUDIENCES: AnnouncementAudience[] =
  ['AllGuardians', 'ClassGuardians', 'SpecificGuardian', 'AllStaff'];


/**
 * Announcements, the calendar, incidents, the reports and the audit trail (§4.11.6, §4.11.7).
 *
 * Grouped as one section because they are all "what happened and what is coming", and because each on its
 * own is a single table that would not justify a place in the navigation. A report's tab shows only to someone
 * holding `reports.view` and the permission of what it counts, as the API asks for both (FR-ADM-20).
 */
export function Reports() {
  const { t } = useI18n();
  // A record's back link names the tab it came from: an incident opens on the incidents.
  const [params] = useSearchParams();
  const [tab, setTab] = useState<Tab>(() => {
    const named = params.get('tab') as Tab;
    return TABS.includes(named) ? named : 'announcements';
  });
  const canAudit = usePermission(Permissions.tenant.viewAudit);
  // Every report asks for reports.view beside the permission of what it counts; the page itself does not, since it
  // also holds the announcements, the calendar and the incidents.
  const canReport = usePermission(Permissions.tenant.viewReports);
  const canAttendance = usePermission(Permissions.attendance.viewAll) && canReport;
  const canDailyCare = usePermission(Permissions.daily.viewAll) && canReport;
  const canRoster = usePermission(Permissions.children.update) && canReport;

  const allowed: Record<Tab, boolean> = {
    announcements: true, calendar: true, incidents: true,
    attendance: canAttendance, dailyCare: canDailyCare, roster: canRoster, audit: canAudit,
  };
  const tabs = TABS.filter((value) => allowed[value]);

  return (
    <Stack>
      <PageHeader title={t('nav.reports')} />

      <Row>
        {tabs.map((value) => (
          <Button
            key={value}
            variant={tab === value ? 'primary' : 'ghost'}
            onClick={() => setTab(value)}
          >
            {t(TAB_TITLES[value])}
          </Button>
        ))}
      </Row>

      {/* `?tab=` can name a tab the caller may not see, so what is drawn is asked of `allowed` too. */}
      {allowed[tab] && (
        <>
          {tab === 'announcements' && <Announcements />}
          {tab === 'calendar' && <CalendarManager />}
          {tab === 'incidents' && <Incidents />}
          {tab === 'attendance' && <AttendanceReport />}
          {tab === 'dailyCare' && <DailyCareReport />}
          {tab === 'roster' && <RosterReport />}
          {tab === 'audit' && <Audit />}
        </>
      )}
    </Stack>
  );
}

/** Composing and tracking announcements (FR-MSG-06, FR-MSG-07). */
function Announcements() {
  const api = useApi();
  const { t, formatDateTime, formatNumber } = useI18n();
  const queryClient = useQueryClient();
  const canBroadcast = usePermission(Permissions.messages.classBroadcast);
  const reachesEveryone = usePermission(Permissions.messages.tenantBroadcast);

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [audience, setAudience] = useState<AnnouncementAudience>('AllGuardians');
  const [classId, setClassId] = useState<number | ''>('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  // Both or neither: whichever is filled makes the other required, so a date alone never sends now.
  const scheduledAt = scheduledDate && scheduledTime ? `${scheduledDate}T${scheduledTime}` : '';

  const classes = useQuery({
    queryKey: ['classes', { status: 'Active' }],
    queryFn: () => api.classes.list({ status: 'Active' }),
  });

  const announcements = useQuery({
    queryKey: ['announcements'],
    queryFn: () => api.announcements.list(),
  });

  const create = useMutation({
    mutationFn: () => api.announcements.create({
      title,
      body,
      audience,
      classId: audience === 'ClassGuardians' && classId !== '' ? classId : null,
      membershipId: null,
      scheduledAt: scheduledAt ? new Date(scheduledAt).toISOString() : null,
      calendarEvent: null,
    }),
    onSuccess: async () => {
      setTitle('');
      setBody('');
      setScheduledDate('');
      setScheduledTime('');
      await queryClient.invalidateQueries({ queryKey: ['announcements'] });
    },
  });

  // A scheduled announcement is held until its time, when the server's scheduler sends it (within a minute).
  // "Send now" is for sending it early; the same call sends a draft.
  const send = useMutation({
    mutationFn: (id: number) => api.announcements.send(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['announcements'] }),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (title.trim() && body.trim()) create.mutate();
  };

  return (
    <Stack>
      {canBroadcast && (
        <Card title={t('announcements.new')}>
          <form onSubmit={submit}>
            <Stack>
              {create.isError && <ErrorState error={create.error} />}

              <Field label={t('announcements.subject')} required>
                {(props) => (
                  <Input {...props} value={title} onChange={(event) => setTitle(event.target.value)} required />
                )}
              </Field>

              <Field label={t('announcements.body')} required>
                {(props) => (
                  <Textarea {...props}
                    rows={4}
                    value={body}
                    onChange={(event) => setBody(event.target.value)}
                    required
                  />
                )}
              </Field>

              <Field label={t('announcements.audience')}>
                {(props) => (
                  <Select {...props}
                    value={audience}
                    onChange={(event) => setAudience(event.target.value as AnnouncementAudience)}
                  >
                    {AUDIENCES.map((value) => (
                      <option key={value} value={value}>{t(`announcements.audience.${value}`)}</option>
                    ))}
                  </Select>
                )}
              </Field>

              {audience === 'ClassGuardians' && (
                <Field label={t('class.one')} required>
                  {(props) => (
                    <Select {...props}
                      value={classId}
                      onChange={(event) =>
                        setClassId(event.target.value === '' ? '' : Number(event.target.value))}
                    >
                      <option value="">—</option>
                      {/* Without the wider authority the server takes only the sender's own classes. */}
                      {(classes.data?.items ?? [])
                        .filter((klass) => reachesEveryone || klass.isAssigned)
                        .map((klass) => (
                          <option key={klass.id} value={klass.id}>{klass.name}</option>
                        ))}
                    </Select>
                  )}
                </Field>
              )}

              {/* Left empty, it goes out now. A future moment holds it until then (FR-MSG-07). */}
              <Row>
                <Field label={t('announcements.schedule')} required={Boolean(scheduledTime)}>
                  {(props) => <DateInput {...props} value={scheduledDate} onChange={setScheduledDate} />}
                </Field>
                <Field label={t('announcements.scheduleTime')} required={Boolean(scheduledDate)}>
                  {(props) => (
                    <Input {...props} type="time" value={scheduledTime} onChange={(event) => setScheduledTime(event.target.value)} />
                  )}
                </Field>
              </Row>

              <Row>
                <Button type="submit" busy={create.isPending} busyLabel={t('action.saving')}>
                  {scheduledAt ? t('action.save') : t('announcements.sendNow')}
                </Button>
              </Row>
            </Stack>
          </form>
        </Card>
      )}

      {send.isError && <ErrorState error={send.error} />}

      <Card flush>
        <DataTable
          caption={t('announcements.title')}
          rows={announcements.data?.items}
          rowKey={(announcement) => announcement.id}
          isLoading={announcements.isPending}
          error={announcements.error}
          onRetry={() => void announcements.refetch()}
          empty={t('announcements.empty')}
          columns={[
            { key: 'title', header: t('announcements.subject'), render: (a) => a.title },
            {
              key: 'audience',
              header: t('announcements.audience'),
              render: (a) => t(`announcements.audience.${a.audience}`),
            },
            {
              key: 'sent',
              header: t('audit.when'),
              render: (a) => a.sentAt
                ? formatDateTime(a.sentAt)
                : a.scheduledAt
                  ? <Badge tone="info">{formatDateTime(a.scheduledAt)}</Badge>
                  : <Badge tone="neutral">{t('status.Draft')}</Badge>,
            },
            {
              // FR-MSG-07: the sender sees who it reached and who has read it.
              key: 'delivery',
              header: t('announcements.delivery', { delivered: '', total: '', read: '' }).trim(),
              render: (a) => t('announcements.delivery', {
                delivered: formatNumber(a.deliveredCount),
                total: formatNumber(a.targetCount),
                read: formatNumber(a.readCount),
              }),
            },
            ...(canBroadcast ? [{
              key: 'send',
              header: <span className="dg-visually-hidden">{t('announcements.sendNow')}</span>,
              render: (a: AnnouncementResponse) => a.sentAt ? null : (
                <Button
                  variant="secondary"
                  busy={send.isPending && send.variables === a.id}
                  disabled={send.isPending}
                  onClick={() => send.mutate(a.id)}
                >
                  {t('announcements.sendNow')}
                </Button>
              ),
            }] : []),
          ]}
        />
      </Card>
    </Stack>
  );
}

/** Incidents, with the ones a family has not yet acknowledged first (FR-CHD-12). */
function Incidents() {
  const api = useApi();
  const { t, formatDateTime } = useI18n();
  // The record as a PDF for the nursery's file, staff only (Q16, Q41); families read it on screen.
  const canPrint = usePermission(Permissions.health.view);
  const printed = usePrintedFile();

  const incidents = useQuery({
    queryKey: ['incidents'],
    queryFn: () => api.health.incidents({ pageSize: 50 }),
  });

  return (
    <Stack>
      {printed.error != null && <ErrorState error={printed.error} />}
      <Card flush>
        <DataTable
          caption={t('incidents.title')}
          rows={incidents.data?.items}
          rowKey={(incident) => incident.id}
          isLoading={incidents.isPending}
          error={incidents.error}
          onRetry={() => void incidents.refetch()}
          empty={t('incidents.empty')}
          columns={[
            { key: 'when', header: t('audit.when'), render: (i) => formatDateTime(i.occurredAt) },
            { key: 'child', header: t('child.one'), render: (i) => i.childName },
            { key: 'type', header: t('incidents.type'), render: (i) => t(`incidents.type.${i.incidentType}`) },
            {
              key: 'severity',
              header: t('incidents.severity'),
              render: (i) => (
                <Badge tone={incidentTone(i.severity)}>
                  {t(`incidents.severity.${i.severity}`)}
                </Badge>
              ),
            },
            {
              key: 'ack',
              header: t('incidents.acknowledge'),
              render: (i) => i.guardianAcknowledgedAt
                ? t('incidents.acknowledged', { date: formatDateTime(i.guardianAcknowledgedAt) })
                : <Badge tone="warning">{t('incidents.awaiting')}</Badge>,
            },
            {
              key: 'open',
              header: '',
              render: (i) => (
                <Link className="dg-button dg-button--ghost" to={`/incidents/${i.id}`}>
                  {i.closedAt ? t('incidents.open') : t('incidents.openToClose')}
                </Link>
              ),
            },
            ...(canPrint ? [{
              key: 'print',
              header: '',
              render: (i: IncidentResponse) => {
                const fileKey = `incident-${i.id}`;
                const busy = printed.busy === fileKey;
                return (
                  <Button
                    variant="ghost"
                    disabled={busy}
                    aria-label={t('incidents.printOf', { name: i.childName })}
                    onClick={() => void printed.open(fileKey, () => api.health.incidentPdf(i.id))}
                  >
                    {t(busy ? 'action.printing' : 'action.print')}
                  </Button>
                );
              },
            }] : []),
          ]}
        />
      </Card>
    </Stack>
  );
}

/** The tenant's own audit trail (FR-ADM-19). */
function Audit() {
  const api = useApi();
  const { t, formatDateTime } = useI18n();
  const [page, setPage] = useState(1);

  const audit = useQuery({
    queryKey: ['admin', 'audit', page],
    queryFn: () => api.admin.audit({ page }),
  });
  useClampedPage(page, setPage, audit.data?.totalPages);

  return (
    <Stack>
      <Card flush>
        <DataTable
          caption={t('audit.title')}
          rows={audit.data?.items}
          rowKey={(entry) => entry.id}
          isLoading={audit.isPending}
          error={audit.error}
          onRetry={() => void audit.refetch()}
          empty={t('audit.empty')}
          columns={[
            { key: 'when', header: t('audit.when'), render: (e) => formatDateTime(e.at) },
            { key: 'actor', header: t('audit.actor'), render: (e) => e.actorName ?? '—' },
            {
              key: 'entity',
              header: t('audit.entity'),
              render: (e) => `${e.entityName ?? '—'}${e.entityId ? ` #${e.entityId}` : ''}`,
            },
            { key: 'action', header: t('audit.action'), render: (e) => e.action },
            {
              // The endpoint's own description, which is why every endpoint is required to carry one.
              key: 'endpoint',
              header: '',
              render: (e) => <span className="dg-muted dg-text-sm">{e.endpoint ?? ''}</span>,
            },
          ]}
        />
      </Card>

      <Pager page={page} totalPages={audit.data?.totalPages} onPage={setPage} />
    </Stack>
  );
}
